import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { ArrowLeft, Slack, Plus, Trash2, Copy, Check, Loader2, ExternalLink, ShieldCheck, MousePointerClick, Hourglass, Send } from "lucide-react";

/**
 * /settings/slack — configure Slack slash-command bridge.
 */
export default function SlackSettings() {
  const [cfg, setCfg] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [secret, setSecret] = useState("");
  const [approvalUrl, setApprovalUrl] = useState("");
  const [map, setMap] = useState([]);  // [{slug, workflow_id}]
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const load = useCallback(() => {
    Promise.all([
      api.get("/slack/config").then((r) => r.data),
      api.get("/workflows").then((r) => r.data).catch(() => ({ workflows: [] })),
    ]).then(([c, wfs]) => {
      setCfg(c);
      setWorkflows(wfs.workflows || []);
      if (c.configured) {
        setTeamId(c.team_id || "");
        setApprovalUrl(c.approval_webhook_url || "");
        const arr = Object.entries(c.workflow_map || {}).map(([slug, workflow_id]) => ({ slug, workflow_id }));
        setMap(arr.length ? arr : [{ slug: "", workflow_id: "" }]);
      } else {
        setMap([{ slug: "", workflow_id: "" }]);
      }
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const webhookUrl = `${API}/slack/commands`;
  const interactionsUrl = `${API}/slack/interactions`;

  const save = async () => {
    setSaving(true);
    try {
      const workflow_map = {};
      for (const { slug, workflow_id } of map) {
        if (slug.trim() && workflow_id) workflow_map[slug.trim()] = workflow_id;
      }
      const body = { team_id: teamId.trim(), workflow_map, approval_webhook_url: approvalUrl.trim() };
      // Only send signing_secret when the user typed a new one; server preserves the stored value otherwise.
      if (secret.trim()) body.signing_secret = secret.trim();
      await api.post("/slack/config", body);
      setSecret("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : (d || "Save failed");
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Slack? Your slash commands will stop working.")) return;
    await api.delete("/slack/config");
    setTeamId(""); setSecret(""); setMap([{ slug: "", workflow_id: "" }]);
    load();
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="slack-back">
          <ArrowLeft size={14} strokeWidth={2.5} /> Dashboard
        </Link>
      </div>
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <Slack size={12} strokeWidth={2.5} /> Slack Integration
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="slack-title">Run workflows from Slack</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Wire up an <b>/aimedley</b> slash command in your workspace. Team members can then trigger any workflow with{" "}
          <code className="text-xs bg-slate-100 border border-slate-200 rounded px-1 py-0.5">/aimedley &lt;slug&gt; &lt;input&gt;</code>.
          Results are posted back to the same channel.
        </p>
      </div>

      {/* Step 1: Webhook URL */}
      <div className="brut-card p-4 space-y-2">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">
          1. Point your Slack app at this webhook
        </div>
        <div className="flex items-center gap-2">
          <code className="brut-input flex-1 font-mono text-xs bg-slate-50 truncate" data-testid="slack-webhook-url">{webhookUrl}</code>
          <button onClick={() => copy("cmd", webhookUrl)} className="brut-btn text-xs" data-testid="slack-copy-url">
            {copied === "cmd" ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={3} />} {copied === "cmd" ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Go to{" "}
          <a className="underline" href="https://api.slack.com/apps" target="_blank" rel="noreferrer">
            api.slack.com/apps <ExternalLink size={9} className="inline" />
          </a>
          {" — "}create a new app → <i>Slash Commands</i> → add <code>/aimedley</code> pointing at the URL above.
        </p>
        <div className="pt-2 border-t-2 border-dashed border-slate-200">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-1 inline-flex items-center gap-1">
            <MousePointerClick size={11} strokeWidth={2.5} /> Interactivity request URL (buttons: Approve / Reject / Re-run)
          </div>
          <div className="flex items-center gap-2">
            <code className="brut-input flex-1 font-mono text-xs bg-slate-50 truncate" data-testid="slack-interactions-url">{interactionsUrl}</code>
            <button onClick={() => copy("int", interactionsUrl)} className="brut-btn text-xs bg-white text-slate-900" data-testid="slack-copy-interactions-url">
              {copied === "int" ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={3} />} {copied === "int" ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            In your Slack app → <i>Interactivity &amp; Shortcuts</i> → toggle on → paste this as the Request URL. Approvals resolve without leaving Slack.
          </p>
        </div>
      </div>

      {/* Step 2: Team ID + Signing Secret */}
      <div className="brut-card p-4 space-y-4">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">
          2. Paste your Slack credentials
        </div>
        <Field label="Team ID" required>
          <input
            className="brut-input w-full font-mono"
            placeholder="T01234ABC"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            data-testid="slack-team-id"
          />
          <p className="text-xs text-slate-500 mt-1">In Slack, click your workspace name → About this workspace. Or find it in your app URL.</p>
        </Field>
        <Field label={cfg?.configured ? `Signing secret (currently: ${cfg.signing_secret_preview || "hidden"})` : "Signing secret"} required={!cfg?.configured}>
          <input
            type="password"
            className="brut-input w-full font-mono"
            placeholder={cfg?.configured ? "Leave blank to keep current" : "paste from api.slack.com → Basic Information"}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="slack-secret"
            autoComplete="off"
          />
          <p className="text-xs text-slate-500 mt-1">Found at api.slack.com/apps/&lt;your-app&gt;/general → "App Credentials".</p>
        </Field>
        <Field label="Approval requests channel (Incoming Webhook URL)">
          <input
            className="brut-input w-full font-mono text-sm"
            placeholder="https://hooks.slack.com/services/T…/B…/…"
            value={approvalUrl}
            onChange={(e) => setApprovalUrl(e.target.value)}
            data-testid="slack-approval-url"
          />
          <p className="text-xs text-slate-500 mt-1">
            When a run pauses at a <b>Human Approval</b> node, an Approve / Reject card is posted to the channel that triggered it. Runs started from the web or a schedule fall back to this webhook. Add it under your Slack app → <i>Incoming Webhooks</i>.
          </p>
        </Field>
      </div>

      {/* Step 3: Workflow map */}
      <div className="brut-card p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700">
          3. Map slash-command slugs to workflows
        </div>
        <p className="text-xs text-slate-600">
          e.g. type <code className="bg-slate-100 rounded px-1">/aimedley research NVIDIA earnings</code> and the <b>research</b> workflow fires with "NVIDIA earnings" as its input.
        </p>
        <div className="space-y-2" data-testid="slack-map-list">
          {map.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`slack-map-row-${idx}`}>
              <input
                className="brut-input flex-1 font-mono text-sm"
                placeholder="slug (e.g. research)"
                value={row.slug}
                onChange={(e) => {
                  const arr = [...map]; arr[idx] = { ...arr[idx], slug: e.target.value }; setMap(arr);
                }}
                data-testid={`slack-slug-${idx}`}
              />
              <select
                className="brut-input flex-1 text-sm"
                value={row.workflow_id}
                onChange={(e) => {
                  const arr = [...map]; arr[idx] = { ...arr[idx], workflow_id: e.target.value }; setMap(arr);
                }}
                data-testid={`slack-workflow-${idx}`}
              >
                <option value="">— pick a workflow —</option>
                {workflows.map((w) => (
                  <option key={w.workflow_id} value={w.workflow_id}>{w.name}</option>
                ))}
              </select>
              <button
                onClick={() => setMap(map.filter((_, i) => i !== idx))}
                disabled={map.length === 1}
                className="text-red-500 hover:text-red-700 disabled:opacity-30"
                title="Remove"
              >
                <Trash2 size={14} strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => setMap([...map, { slug: "", workflow_id: "" }])}
          className="brut-btn text-xs bg-white text-slate-900"
          data-testid="slack-add-mapping"
        >
          <Plus size={11} strokeWidth={3} /> Add mapping
        </button>
      </div>

      <div className="flex items-center justify-between">
        {cfg?.configured ? (
          <button onClick={disconnect} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs font-bold" data-testid="slack-disconnect">
            <Trash2 size={12} strokeWidth={2.5} /> Disconnect Slack
          </button>
        ) : <div />}
        <button
          onClick={save}
          disabled={saving || !teamId.trim() || (!cfg?.configured && !secret.trim())}
          className="brut-btn text-sm disabled:opacity-40"
          data-testid="slack-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} strokeWidth={3} />} {saving ? "Saving…" : cfg?.configured ? "Update config" : "Save & activate"}
        </button>
      </div>

      <DigestCard hasWebhook={!!approvalUrl.trim()} />
    </div>
  );
}

function DigestCard({ hasWebhook }) {
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/slack/approval-digest/preview").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const sendNow = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/slack/approval-digest"); setResult(data); load(); }
    catch (e) { setResult({ error: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="brut-card p-4 space-y-3" data-testid="digest-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 inline-flex items-center gap-1">
          <Hourglass size={12} strokeWidth={2.5} /> Daily approval digest · 09:00 UTC
        </div>
        <button onClick={sendNow} disabled={busy} className="brut-btn text-xs bg-white text-slate-900" data-testid="digest-send-now">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={2.5} />} Send digest now
        </button>
      </div>
      <p className="text-xs text-slate-600">
        Every morning, runs still paused at a Human Approval node are summarised into the approval channel above and as an in-app notification — so nothing gets stuck.
        {!hasWebhook && <span className="text-amber-700 font-bold"> Add an approval webhook URL to receive it in Slack; the in-app digest works regardless.</span>}
      </p>
      {info && (
        <div className="text-xs font-mono text-slate-600" data-testid="digest-status">
          <span className="font-bold text-slate-900">{info.waiting}</span> waiting now
          {info.last_sent && <> · last sent {new Date(info.last_sent.sent_at).toLocaleString()} ({info.last_sent.slack_posted ? "Slack ✓" : "in-app only"})</>}
        </div>
      )}
      {result && (
        <div className={`text-xs font-mono ${result.error ? "text-red-600" : "text-emerald-600"}`} data-testid="digest-result">
          {result.error ? result.error : result.waiting === 0 ? "Nothing waiting — no digest needed." : `Sent · ${result.waiting} waiting · Slack ${result.slack_posted ? "posted" : "skipped"} · in-app ${result.notified ? "sent" : "failed"}`}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
    </div>
  );
}
